import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Hexagon, Swords, Dumbbell, MonitorPlay, Library as LibIcon, Salad,
  MessageSquare, LogOut, ScanLine, Search, ArrowRight, Plus, X,
  Globe, AppWindow, Trash2, ChevronUp, ChevronDown, Settings2, Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SITES, APPS, launchTarget, openQuery } from "@/data/launcher";
import api from "@/lib/api";

const polar = (i, n, rx, ry, offset = -90) => {
  const a = (offset + i * (360 / n)) * (Math.PI / 180);
  return { x: 50 + rx * Math.cos(a), y: 50 + ry * Math.sin(a) };
};

const SWATCHES = ["#00F0FF", "#66c0f4", "#f16436", "#5865f2", "#1db954", "#ff0000", "#9146ff", "#ffd54a"];

export default function NeuralHub() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [omni, setOmni] = useState("");
  const [custom, setCustom] = useState([]);
  const [manage, setManage] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  const loadNodes = useCallback(async () => {
    try {
      const { data } = await api.get("/hub/nodes");
      setCustom(data.nodes);
      localStorage.setItem("archon_custom_nodes", JSON.stringify(data.nodes));
    } catch { /* */ }
  }, []);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/system/profile"); setUnlocked(!!data.unlocked); } catch { /* */ }
  }, []);
  useEffect(() => { if (user) { load(); loadNodes(); } }, [user, load, loadNodes]);

  const modes = useMemo(() => ([
    { id: "system", label: unlocked ? "System" : "Awaken", icon: unlocked ? Swords : ScanLine, action: () => navigate(unlocked ? "/system" : "/awaken") },
    { id: "training", label: "Training", icon: Dumbbell, action: () => navigate("/training") },
    { id: "coach", label: "Live Coach", icon: MonitorPlay, action: () => navigate("/coach") },
    { id: "library", label: "Library", icon: LibIcon, action: () => navigate("/library") },
    { id: "nutrition", label: "Nutrition", icon: Salad, action: () => navigate("/nutrition") },
    { id: "channel", label: "Channel", icon: MessageSquare, action: () => navigate("/channel") },
  ]), [unlocked, navigate]);

  const defaultTabs = useMemo(() => ([SITES[0], SITES[1], SITES[2], SITES[3], APPS[0], APPS[1], APPS[2], APPS[3]]), []);
  const customTabs = custom.map((c) => ({
    id: c.id, name: c.name, color: c.color, custom: true,
    icon: c.kind === "uri" ? AppWindow : Globe,
    launch: () => launchTarget(c.kind === "uri" ? { uri: c.target } : { web: c.target }),
  }));
  const tabs = [...defaultTabs, ...customTabs];

  const innerPos = modes.map((_, i) => polar(i, modes.length, 26, 30, -90));
  const outerCount = tabs.length + 1; // +1 for the add node
  const outerPos = Array.from({ length: outerCount }, (_, i) => polar(i, outerCount, 45, 46, -68));
  const neurons = useMemo(() => Array.from({ length: 10 }, (_, i) => polar(i, 10, 36, 38, -50 + i * 7)), []);

  const edges = [];
  innerPos.forEach((p) => edges.push({ x1: 50, y1: 50, x2: p.x, y2: p.y }));
  innerPos.forEach((p, i) => { const q = innerPos[(i + 1) % innerPos.length]; edges.push({ x1: p.x, y1: p.y, x2: q.x, y2: q.y }); });
  outerPos.forEach((p, i) => { const q = outerPos[(i + 1) % outerPos.length]; edges.push({ x1: p.x, y1: p.y, x2: q.x, y2: q.y, flow: true }); });
  outerPos.forEach((p) => {
    let best = 0, bd = 1e9;
    innerPos.forEach((ip, j) => { const d = (ip.x - p.x) ** 2 + (ip.y - p.y) ** 2; if (d < bd) { bd = d; best = j; } });
    edges.push({ x1: p.x, y1: p.y, x2: innerPos[best].x, y2: innerPos[best].y, flow: true });
  });

  const submitOmni = (e) => { e.preventDefault(); openQuery(omni); };

  return (
    <div className="min-h-screen bg-[#050505] grid-bg relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 glow-radial pointer-events-none" />
      <header className="relative z-20 flex items-center justify-between px-5 sm:px-8 py-4">
        <div className="flex items-center gap-2">
          <Hexagon className="text-cyan-400" size={20} />
          <span className="font-display font-bold tracking-[0.3em]">ARCHON</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="manage-nodes-btn" onClick={() => setManage(true)} className="mono-label flex items-center gap-1.5 border border-blue-900/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10 transition-all">
            <Settings2 size={13} /> Edit Nodes
          </button>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="text-slate-500 hover:text-red-400"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="relative z-20 px-4 pt-1">
        <form onSubmit={submitOmni} className="max-w-xl mx-auto flex items-center gap-2 bg-[#0A0E17]/80 border border-blue-500/30 focus-within:border-cyan-400 rounded-full px-4 py-2.5 backdrop-blur transition-all" data-testid="omnibox-form">
          <Search size={16} className="text-cyan-400 shrink-0" />
          <input data-testid="omnibox" value={omni} onChange={(e) => setOmni(e.target.value)}
            placeholder="Search the web or type any address…"
            className="flex-1 bg-transparent outline-none text-slate-50 placeholder-slate-500 text-sm" />
          <button type="submit" data-testid="omnibox-go" className="shrink-0 w-8 h-8 rounded-full grid place-items-center bg-blue-600 hover:bg-blue-500 text-white transition-colors"><ArrowRight size={16} /></button>
        </form>
      </div>

      <div className="relative z-10 text-center pt-3">
        <p className="mono-label">SAY “ARCHON” OR TAP A NODE</p>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-5xl flex-1 min-h-[560px]" data-testid="neural-hub">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {edges.map((e, i) => (
            <line key={i} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} stroke="rgba(0,240,255,0.4)" strokeWidth="0.18"
              strokeDasharray={e.flow ? "1 1.4" : undefined} className={e.flow ? "hub-edge hub-edge-2" : "hub-edge"}
              style={{ animationDelay: `${(i % 7) * 0.25}s` }} />
          ))}
          {neurons.map((p, i) => (<circle key={`n${i}`} cx={p.x} cy={p.y} r="0.5" fill="#00F0FF" className="neuron" style={{ animationDelay: `${i * 0.2}s` }} />))}
        </svg>

        <motion.button initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 120 }}
          onClick={() => navigate(unlocked ? "/system" : "/awaken")} data-testid="hub-core"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30" style={{ width: "clamp(84px,13vw,120px)", height: "clamp(84px,13vw,120px)" }}>
          <span className="orb speaking block w-full h-full" />
          <span className="absolute inset-0 grid place-items-center font-display font-bold tracking-widest text-black text-sm">CORE</span>
        </motion.button>

        {modes.map((m, i) => (
          <motion.button key={m.id} data-testid={`hub-node-${m.id}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.12 + i * 0.06, type: "spring", stiffness: 150 }}
            onClick={m.action} className="absolute z-30 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1.5 group" style={{ left: `${innerPos[i].x}%`, top: `${innerPos[i].y}%` }}>
            <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl grid place-items-center glass border border-blue-500/40 group-hover:border-cyan-400/80 group-hover:shadow-[0_0_26px_rgba(0,240,255,0.45)] transition-all duration-300 group-hover:-translate-y-1">
              <m.icon className="text-cyan-300" size={22} />
            </span>
            <span className="font-display font-semibold text-xs text-slate-100">{m.label}</span>
          </motion.button>
        ))}

        {tabs.map((t, i) => (
          <motion.div key={t.id} data-testid={`hub-tab-${t.id}`} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 + i * 0.04, type: "spring", stiffness: 160 }}
            className="absolute z-30 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 group" style={{ left: `${outerPos[i].x}%`, top: `${outerPos[i].y}%` }}>
            <button onClick={() => (t.launch ? t.launch() : launchTarget(t))} title={t.name}
              className="relative w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center bg-[#0A0E17]/90 border border-white/15 group-hover:scale-110 group-hover:border-cyan-400/70 transition-all duration-300"
              style={{ boxShadow: `0 0 14px ${t.color}44` }}>
              <t.icon size={18} style={{ color: t.color }} />
            </button>
            <span className="mono-label opacity-70 group-hover:opacity-100">{t.name}</span>
          </motion.div>
        ))}

        {/* Add node */}
        <motion.button data-testid="hub-add-node" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 + tabs.length * 0.04, type: "spring", stiffness: 160 }}
          onClick={() => setManage(true)} title="Add node"
          className="absolute z-30 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1 group" style={{ left: `${outerPos[tabs.length].x}%`, top: `${outerPos[tabs.length].y}%` }}>
          <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-full grid place-items-center border-2 border-dashed border-cyan-400/50 text-cyan-300 group-hover:scale-110 group-hover:bg-cyan-400/10 transition-all"><Plus size={20} /></span>
          <span className="mono-label opacity-70">Add</span>
        </motion.button>
      </div>

      <AnimatePresence>
        {manage && <ManageModal onClose={() => setManage(false)} custom={custom} reload={loadNodes} />}
      </AnimatePresence>
    </div>
  );
}

function ManageModal({ onClose, custom, reload }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [color, setColor] = useState(SWATCHES[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !target.trim()) { setErr("Enter a name and a link or app address."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/hub/nodes", { name, target, color });
      setName(""); setTarget(""); await reload();
    } catch { setErr("Couldn't add that node."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/hub/nodes/${id}`); await reload(); };
  const move = async (i, dir) => {
    const arr = [...custom];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    await api.post("/hub/nodes/reorder", { ids: arr.map((x) => x.id) });
    await reload();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] grid place-items-center bg-black/70 backdrop-blur-sm px-5" data-testid="manage-modal" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="glass rounded-2xl p-6 max-w-lg w-full border border-cyan-400/30 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-display text-xl font-bold">Customize your Hub</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="manage-close"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-400 mb-5">Pin any website (e.g. <span className="text-cyan-300">twitch.tv</span>) or app link (e.g. <span className="text-cyan-300">steam://open/games</span>).</p>

        <form onSubmit={add} className="space-y-3 mb-6">
          <input data-testid="node-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. My Server)"
            className="w-full bg-slate-950/60 border border-blue-900/50 focus:border-cyan-400 outline-none rounded-lg px-3 py-2.5 text-sm text-slate-50" />
          <input data-testid="node-target-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Link or app address"
            className="w-full bg-slate-950/60 border border-blue-900/50 focus:border-cyan-400 outline-none rounded-lg px-3 py-2.5 text-sm text-slate-50" />
          <div className="flex items-center gap-2">
            {SWATCHES.map((c) => (
              <button type="button" key={c} onClick={() => setColor(c)} data-testid={`swatch-${c}`}
                className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110" style={{ background: c, borderColor: color === c ? "#fff" : "transparent" }} />
            ))}
          </div>
          {err && <p data-testid="node-error" className="text-red-400 text-sm">{err}</p>}
          <button type="submit" data-testid="add-node-submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white py-2.5 rounded-lg font-medium transition-all">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add Node
          </button>
        </form>

        <p className="mono-label mb-2">YOUR NODES</p>
        <div className="space-y-2">
          {custom.map((c, i) => (
            <div key={c.id} className="flex items-center gap-3 border border-blue-900/30 rounded-lg px-3 py-2" data-testid={`manage-node-${c.id}`}>
              <span className="w-6 h-6 rounded-full shrink-0" style={{ background: c.color }} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-100 truncate">{c.name}</span>
                <span className="block text-[0.7rem] text-slate-500 truncate">{c.target}</span>
              </span>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-cyan-300 disabled:opacity-20" data-testid={`node-up-${c.id}`}><ChevronUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === custom.length - 1} className="text-slate-500 hover:text-cyan-300 disabled:opacity-20" data-testid={`node-down-${c.id}`}><ChevronDown size={16} /></button>
              <button onClick={() => remove(c.id)} className="text-slate-500 hover:text-red-400" data-testid={`node-delete-${c.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
          {custom.length === 0 && <p className="text-slate-600 text-sm">No custom nodes yet. Add your favorite games & sites above.</p>}
        </div>
      </motion.div>
    </motion.div>
  );
}
